import {Inject, Injectable} from "@angular/core";
import {DOCUMENT, Location} from "@angular/common";
import {NavigationEnd, Router} from "@angular/router";
import {LocalizeRouterService} from "@gilsdav/ngx-translate-router";
import {filter, take} from "rxjs/operators";
import {environment} from "src/environments/environment";
import {Meta, Title} from "@angular/platform-browser";
import {TranslateService} from "@ngx-translate/core";

interface PageMetadata {
    title: string;
    image: string;
    description: string;
    alternatesUrls?: AlternateUrl[];
}

export interface AlternateUrl {
    href: string;
    hrefLang: string;
}

interface OpenGraphMetadata {
    type: string;
    siteName: string;
    title: string;
    url: string;
    image: string;
    description: string;
}

interface TwitterCardMetadata {
    card: string;
    url: string;
    title: string;
    description: string;
    image: string;
}

@Injectable()
export class SeoService {

    private readonly ORIGIN = environment.BASE_URL
    private readonly CANONICAL_LANG = this.localizeRouterService.parser.currentLang;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private router: Router,
        private localizeRouterService: LocalizeRouterService,
        private meta: Meta,
        private title: Title,
        private location: Location,
        private translate: TranslateService,
    ) {


    }

    public setMetadata(metadata: PageMetadata) {
        this.title.setTitle(`${metadata.title} | ${environment.BRAND_NAME} `);
        const descriptionHtmlMetaElement = this.meta.updateTag({
            name: 'description',
            content: metadata.description,
        });
        const url  = environment.BASE_URL + this.location.path()
        const openGraphHtmlMetaElements = this.setOpenGraph({
            type: 'website',
            siteName: environment.SITE_NAME,
            title: metadata.title,
            url: url,
            image: metadata.image,
            description: metadata.description,
        });
        const twitterCardHtmlMetaElements = this.setTwitterCard({
            card: 'summary_large_image',
            url: url,
            title: metadata.title,
            description: metadata.description,
            image: metadata.image,
        });
        const htmlMetaElements = [...openGraphHtmlMetaElements, ...twitterCardHtmlMetaElements];

        const alternatesAbsoluteUrls = metadata.alternatesUrls?.map(
            alternate => ({...alternate, href: [this.ORIGIN,alternate.href].join('/')})
        )
        const hasAlternateUrls = alternatesAbsoluteUrls != null;
        if (hasAlternateUrls) {
            this.addLanguageAlternatives([...alternatesAbsoluteUrls].filter(alternate => alternate.hrefLang !== this.CANONICAL_LANG));
        }

        const canonicalUrl = alternatesAbsoluteUrls?.find(alternate => alternate.hrefLang === this.CANONICAL_LANG);
        const hasCanonicalUrl = canonicalUrl != null;
        if (hasCanonicalUrl) this.addCanonical(canonicalUrl);

        this.router.events.pipe(
            filter( e => e instanceof NavigationEnd),
            take(1),
        ).subscribe({
            next: () => {
            this.title.setTitle(this.translate.instant('seo.default_title'));
            this.removeMetaElements(htmlMetaElements);
            if (descriptionHtmlMetaElement) this.meta.removeTagElement(descriptionHtmlMetaElement);
            if (hasAlternateUrls) this.removeLanguageAlternatives();
            if (hasCanonicalUrl) this.removeCanonical();
        }})
    }
    public setTitle(title: string) {
        this.title.setTitle(`${title}`);
        //The subscription is for when you browse so that it changes to the appropriate title
        this.router.events.pipe(
            filter( e => e instanceof NavigationEnd),
            take(1),
        ).subscribe({
            next: () => {
            this.title.setTitle(this.translate.instant('seo.default_title'));
        }})
    }
    public setDescription(description: string) {
        this.meta.updateTag({
            name: 'description',
            content: description,
        });
    }
    private setOpenGraph(metadata: OpenGraphMetadata): HTMLMetaElement[] {
        const tags = [
            {property: 'og:type',content: metadata.type},
            {property: 'og:site_name', content: metadata.siteName},
            {property: 'og:title', content: metadata.title},
            {property: 'og:url', content: metadata.url},
            {property: 'og:image', content: metadata.image},
            {property: 'og:description', content: metadata.description},
        ];
        const htmlMetaElements = tags.map(tag => this.meta.updateTag(tag));
        return htmlMetaElements.filter(element => element != null) as HTMLMetaElement[];
    }

    private setTwitterCard(metadata: TwitterCardMetadata) {
        const tags = [
            {name: 'twitter:card',content: metadata.card},
            {name: 'twitter:url', content: metadata.url},
            {name: 'twitter:title', content: metadata.title},
            {name: 'twitter:text:description', content: metadata.description},
            {name: 'twitter:description', content: metadata.description},
            {name: 'twitter:image:src', content: metadata.image},
            {name: 'twitter:image', content: metadata.image},

        ];
        const htmlMetaElements = tags.map(tag => this.meta.updateTag(tag));
        return htmlMetaElements.filter(element => element != null) as HTMLMetaElement[];
    }

    private removeMetaElements(metaElements: HTMLMetaElement[]) {
        metaElements.forEach(metaElement => this.meta.removeTagElement(metaElement));
    }

    private addLanguageAlternativeUrl(alternateUrl: AlternateUrl): void {
        const link: HTMLLinkElement = this.document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', alternateUrl.hrefLang);
        link.setAttribute('href', alternateUrl.href);
        this.document.head.appendChild(link);
    }

    private addLanguageAlternatives(alternateUrls: AlternateUrl[]) {
        alternateUrls.forEach(alternateUrl => this.addLanguageAlternativeUrl(alternateUrl))
    }

    private removeLanguageAlternatives() {
        const selector = `link[rel='alternate']`;
        const languageAlternativeElements = this.document.head.querySelectorAll(selector);
        languageAlternativeElements.forEach(element => element.remove());
    }

    private addCanonical(alternate: AlternateUrl) {
        const link: HTMLLinkElement = this.document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('hreflang', alternate.hrefLang);
        link.setAttribute('href', alternate.href);
        this.document.head.appendChild(link);
    }

    private removeCanonical() {
        const selector = `link[rel='canonical']`;
        const languageAlternativeElements = this.document.head.querySelectorAll(selector);
        languageAlternativeElements.forEach(element => element.remove());
    }

}
